import { type AxiosError } from 'axios';
import { toast } from 'sonner';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { i18n } from '@avanta/shared/i18n';
import { Sentry } from '@avanta/shared/sentry';

type ServerError = {
  message: string;
  action?: (message: string) => void;
};

type MessageError = {
  [key: string]: ServerError;
};

export type ApiErrorData = { message?: string };

type ErrorServiceTypes = {
  handlerError: (error: AxiosError<ApiErrorData>) => {
    customError: ServerError | undefined;
    errorMessage: string;
  };
  handlerErrorRequest: (error: AxiosError<ApiErrorData>) => void;
};

const getDefaultErrorMessage = (): string => i18n.t('Common.error', { ns: LOCIZE_NS.COMMON });

const messageErrors = (error: AxiosError<ApiErrorData>): MessageError => {
  const payload = JSON.parse(error.config?.data || '{}');
  return {
    ['Email domain has no MX and no A/AAAA records.']: {
      message: i18n.t('SignupForm.Form.email.error.invalid', { ns: LOCIZE_NS.AUTH }),
    },
    ['Employee code not accepted on this enterprise']: {
      message: i18n.t('SignupForm.Form.nif.error.invalid', {
        nif: payload.nif,
        ns: LOCIZE_NS.AUTH,
      }),
    },
    ['Employee code already consumed']: {
      message: i18n.t('SignupForm.Form.employeeCode.error.consumed', { ns: LOCIZE_NS.AUTH }),
    },
  };
};

function getCustomError(error: AxiosError<ApiErrorData>): ServerError | undefined {
  const message = error?.response?.data?.message;

  if (message && messageErrors(error)?.[message]) {
    return messageErrors(error)?.[message];
  }

  return { message: getDefaultErrorMessage() };
}

export const ErrorService: ErrorServiceTypes = {
  handlerError: error => {
    const customError = getCustomError(error);
    const errorMessage = customError?.message ?? getDefaultErrorMessage();
    return {
      customError,
      errorMessage,
    };
  },

  handlerErrorRequest: error => {
    const { customError, errorMessage } = ErrorService.handlerError(error);
    Sentry.captureException(error, {
      tags: {
        type: 'api_error',
        status: error.response?.status,
        endpoint: error.config?.url,
      },
      extra: {
        method: error.config?.method,
        responseData: error.response?.data,
      },
    });

    if (customError?.action) {
      customError.action(errorMessage);
      return;
    }
    toast.error(errorMessage);
  },
};
